import { cn } from '@april9au/react-common/utils';

import type { CtaLink } from './cta-link';
import { CtaLinkButton } from './cta-link';

/**
 * Full-bleed image break, shorter than the viewport-height `HeroSection`.
 * Reusable for area/lifestyle highlights (e.g. a South Burnett landscape
 * shot) or a mid-page moment that needs a photo with a short message over it.
 */
export function ImageBanner({
  id,
  eyebrow,
  heading,
  description,
  cta,
  secondaryCta,
  image,
  imageAlt,
  align = 'left',
  height = 'md',
  dim = false,
  className,
}: {
  id?: string;
  eyebrow?: string;
  heading: string;
  description?: string;
  cta?: CtaLink;
  secondaryCta?: CtaLink;
  image: string;
  imageAlt?: string;
  align?: 'left' | 'center';
  height?: 'md' | 'lg';
  /** Adds a flat dimming layer over the photo, for images that are too bright/busy for the text to sit on comfortably. Off by default. */
  dim?: boolean;
  className?: string;
}) {
  return (
    <section id={id} className={cn('image-banner', height === 'lg' && 'image-banner--lg', className)}>
      <img src={image} alt={imageAlt ?? ''} className="image-banner__media" />
      {dim ? <span className="image-banner__filter" aria-hidden="true" /> : null}
      <span className="image-banner__scrim" aria-hidden="true" />

      <div className={cn('image-banner__content container', align === 'center' && 'image-banner__content--center')}>
        {eyebrow ? (
          <p className="hero__tagline text-eyebrow">
            <span>{eyebrow}</span>
          </p>
        ) : null}

        <h2 className="text-h1">{heading}</h2>

        {description ? (
          <p className={cn('text-lead mt-4 max-w-xl', align === 'center' && 'mx-auto')}>{description}</p>
        ) : null}

        {cta || secondaryCta ? (
          <div className={cn('mt-8 flex flex-wrap gap-4', align === 'center' && 'justify-center')}>
            {cta ? <CtaLinkButton cta={cta} size="lg" /> : null}
            {secondaryCta ? <CtaLinkButton cta={secondaryCta} size="lg" variant="secondary" /> : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
